/**
 * Desktop Service
 * Handles Firestore operations for the admin desktop shortcuts
 */

import { collection, doc, setDoc, deleteDoc, onSnapshot, serverTimestamp } from 'firebase/firestore'
import { db } from '../firebase/firebase'

/**
 * Get reference to Desktop subcollection for a user
 * @param {string} userId - User UID
 * @returns {CollectionReference}
 */
function getDesktopCollection(userId) {
  return collection(db, 'Users', userId, 'Desktop')
}

/**
 * Save or update a desktop shortcut
 * @param {string} userId - User UID
 * @param {Object} shortcut - Shortcut data
 * @returns {Promise<string>} Shortcut ID
 */
export async function saveDesktopShortcut(userId, shortcut) {
  const docRef = doc(getDesktopCollection(userId), shortcut.id)

  await setDoc(
    docRef,
    {
      ...shortcut,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  )

  return shortcut.id
}

/**
 * Update shortcut position after drag
 * @param {string} userId - User UID
 * @param {string} shortcutId - Shortcut ID
 * @param {Object} position - { x, y, gridCol?, gridRow? } coordinates and grid info
 * @returns {Promise<void>}
 */
export async function updateShortcutPosition(userId, shortcutId, position) {
  const docRef = doc(getDesktopCollection(userId), shortcutId)

  const updateData = {
    position: { x: position.x, y: position.y },
    updatedAt: serverTimestamp(),
  }

  // Include grid cell info if provided
  if (position.gridCol !== undefined) {
    updateData.gridCol = position.gridCol
  }
  if (position.gridRow !== undefined) {
    updateData.gridRow = position.gridRow
  }

  await setDoc(docRef, updateData, { merge: true })
}

/**
 * Delete a shortcut from desktop
 * @param {string} userId - User UID
 * @param {string} shortcutId - Shortcut ID
 * @returns {Promise<void>}
 */
export async function deleteDesktopShortcut(userId, shortcutId) {
  const docRef = doc(getDesktopCollection(userId), shortcutId)
  await deleteDoc(docRef)
}

/**
 * Subscribe to desktop shortcuts in real-time
 * @param {string} userId - User UID
 * @param {Function} callback - Called with (shortcuts, error)
 * @returns {Function} Unsubscribe function
 */
export function subscribeToDesktopShortcuts(userId, callback) {
  const colRef = getDesktopCollection(userId)

  return onSnapshot(
    colRef,
    (snapshot) => {
      const shortcuts = {}
      snapshot.forEach((doc) => {
        shortcuts[doc.id] = { id: doc.id, ...doc.data() }
      })
      callback(shortcuts, null)
    },
    (error) => {
      console.error('Error subscribing to desktop shortcuts:', error)
      callback({}, error)
    }
  )
}
